<template>
    <modal id="finance-account-import"
           size="large"
           v-model="showModal"
           :title="$t('import')"
           :preloader="preloader"
           :loading="loading"
           :btn-label="$t('import')"
           @submit="submitData">
        <form ref="form" :data-url='formUrl' @submit.prevent="submitData">
            
            <div class="row">
                <div class="col-md-12">
                    <div class="form-group">
                        <label>{{ $t('file') }} <span class="text-danger">*</span></label>
                        <div class="custom-file" :class="{'border border-danger rounded': errors.import_file}" style="height: 44px;">
                            <input type="file" id="file" v-on:change="change" class="custom-file-input" accept=".csv">
                            <label for="file" class="custom-file-label" v-text="placeholder" style="padding: 10px 14px !important;"/>
                        </div>
                        <small v-if="errors.import_file" class="text-danger">
                            {{ errors.import_file[0] }}
                        </small>
                        <small v-else class="form-text text-muted">
                            {{ $t('please_select_csv_file') }}
                        </small>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-12 mb-3">
                    <h5>{{ $t('map_columns') }}</h5>
                    <p class="text-muted small">{{ $t('map_csv_columns_to_system_fields') }}</p>
                </div>

                <div class="col-md-6" v-for="field in mappingFields" :key="field.key">
                    <div class="form-group">
                        <label>
                            {{ field.label }}
                            <span v-if="field.required" class="text-danger">*</span>
                        </label>
                        <select 
                            class="form-control" 
                            :class="{'border-danger': errors['mapping.' + field.key]}"
                            v-model="mapping[field.key]" 
                            :disabled="headers.length === 0">
                            <option value="" disabled>{{ $t('select') }}</option>
                            <option v-for="(header, index) in headers" :key="index" :value="index">
                                {{ header }}
                            </option>
                        </select>
                        <small v-if="errors['mapping.' + field.key]" class="text-danger">
                            {{ errors['mapping.' + field.key][0] }}
                        </small>
                    </div>
                </div>

                <div class="col-12 mt-3" v-if="headers.length > 0">
                    <h6>{{ $t('preview_data') }}</h6>
                    <div class="table-responsive">
                        <table class="table table-bordered table-sm">
                            <thead>
                                <tr>
                                    <th v-for="(header, index) in headers" :key="index">{{ header }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(row, rowIndex) in previewData" :key="rowIndex">
                                    <td v-for="(cell, cellIndex) in row" :key="cellIndex">{{ cell }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </form>
    </modal>
</template>

<script>
import ModalMixin from "../../../../common/Mixin/Global/ModalMixin";
import FormHelperMixins from "../../../../common/Mixin/Global/FormHelperMixins";
import {TENANT_BASE_URL} from "../../../../common/Config/UrlHelper";
import {axiosPost} from "../../../../common/Helper/AxiosHelper";

export default {
    name  : "FinanceAccountImport",
    mixins: [FormHelperMixins, ModalMixin],
    props: {
        accountId: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            TENANT_BASE_URL,
            fieldValue : null,
            placeholder: this.$placeholder('file', 'csv'),
            headers: [],
            previewData: [],
            mapping: {
                transaction_date: '',
                transaction_details: '',
                withdrawal_amount: '',
                deposit_amount: '',
                balance: '',
                description: '',
            },
            requiredFields: ['transaction_date', 'withdrawal_amount', 'deposit_amount', 'balance'],
            mappingFields: [
                { key: 'transaction_date', label: this.$t('date'), required: true },
                { key: 'transaction_details', label: this.$t('transaction_details') },
                { key: 'withdrawal_amount', label: this.$t('withdrawal_amount'), required: true },
                { key: 'deposit_amount', label: this.$t('deposit_amount'), required: true },
                { key: 'balance', label: this.$t('balance'), required: true },
                { key: 'description', label: this.$t('description') }
            ]
        }
    },
    computed: {
        formUrl() {
            return `${TENANT_BASE_URL}app/finance-accounts/${this.accountId}/import`;
        }
    },
    methods: {
        change(event) {
            const placeholder = this.$placeholder('file', 'csv')
            this.fieldValue   = event.target.files[0];

            if (this.fieldValue) {
                this.placeholder = this.fieldValue?.name || placeholder;
                this.parseCsv();
            } else {
                this.placeholder = placeholder;
                this.fieldValue  = null;
                this.headers = [];
                this.previewData = [];
            }
        },
        submitData() {
            this.handleImport();
        },
        parseCsv() {
            if (!this.fieldValue) return;

            const reader = new FileReader();
            reader.onload = (e) => {
                const content = e.target.result;
                const rows = this.csvToArray(content);
                
                if (rows.length > 0) {
                    this.headers = rows[0];
                    this.previewData = rows.slice(1, 6); // Get next 5 rows
                    this.autoMapFields();
                }
            };
            
            // Try to read with Shift_JIS first for Japanese CSVs
            // Most bank CSVs from Japan use Shift-JIS encoding
            reader.readAsText(this.fieldValue, 'Shift_JIS'); 
        },
        csvToArray(str, delimiter = ",") {
            // Simple CSV parser handling quoted fields
            const pattern = new RegExp(
                (
                    "(\\" + delimiter + "|\\r?\\n|\\r|^)" +
                    "(?:\"([^\"]*(?:\"\"[^\"]*)*)\"|" +
                    "([^\"\\" + delimiter + "\\r\\n]*))"
                ),
                "gi"
            );
            const rows = [[]];
            let matches = null;
            while (matches = pattern.exec(str)) {
                const matchedDelimiter = matches[1];
                if (matchedDelimiter.length && matchedDelimiter !== delimiter) {
                    rows.push([]);
                }
                const matchedValue = matches[2] ? matches[2].replace(new RegExp("\"\"", "g"), "\"") : matches[3];
                rows[rows.length - 1].push(matchedValue);
            }
            // Remove empty last row if any
            if (rows.length > 0 && rows[rows.length - 1].length === 0) {
                rows.pop();
            }
            return rows;
        },
        handleImport() {
            if (!this.fieldValue) {
                 this.$toastr.e(this.$t('please_select_csv_file'));
                 return;
            }

            // Validate required fields
            for (const field of this.requiredFields) {
                if (this.mapping[field] === '' || this.mapping[field] === null) {
                    this.$toastr.e(this.$t('please_map_required_fields'));
                    return;
                }
            }

            this.loading = true;
            
            const formData = new FormData();
            formData.set('import_file', this.fieldValue);
            
            // Append mapping as array
            Object.keys(this.mapping).forEach(key => {
                if (this.mapping[key] !== null) {
                    formData.set(`mapping[${key}]`, this.mapping[key]);
                }
            });

            this.submitFromFixin('post', this.formUrl, formData);
        },
        autoMapFields() {
            // Simple auto-mapping based on name similarity
            this.headers.forEach((header, index) => {
                const lowerHeader = header.toLowerCase();
                
                if (lowerHeader.includes('date') || lowerHeader.includes('ngày')) {
                    if (this.mapping.transaction_date === null) this.mapping.transaction_date = index;
                }
                if (lowerHeader.includes('debit') || lowerHeader.includes('withdrawal') || lowerHeader.includes('nợ') || lowerHeader.includes('chi')) {
                    if (this.mapping.withdrawal_amount === null) this.mapping.withdrawal_amount = index;
                }
                if (lowerHeader.includes('credit') || lowerHeader.includes('deposit') || lowerHeader.includes('có') || lowerHeader.includes('thu')) {
                    if (this.mapping.deposit_amount === null) this.mapping.deposit_amount = index;
                }
                if (lowerHeader.includes('balance') || lowerHeader.includes('dư')) {
                    if (this.mapping.balance === null) this.mapping.balance = index;
                }
                if (lowerHeader.includes('description') || lowerHeader.includes('diễn giải') || lowerHeader.includes('nội dung')) {
                    if (this.mapping.description === null) this.mapping.description = index;
                }
                if (lowerHeader.includes('details') || lowerHeader.includes('chi tiết')) {
                    if (this.mapping.transaction_details === null) this.mapping.transaction_details = index;
                }
            });
        },
        afterSuccess({data}) {
            $('#finance-account-import').modal('hide');
            this.$emit('input', false);
            
            // Show success message with import stats
            const message = this.$t('csv_imported_successfully', {
                total: data.data.total_rows,
                success: data.data.success_count,
                error: data.data.error_count
            });
            this.$toastr.s(message);
            
            const batchId = data.data.batch_id;
            setTimeout(() => {
                window.location.href = `/finance/transaction-imports?batch_id=${batchId}`;
            }, 2000);
        },
        afterSuccessFromGetEditData(response) {
            this.loading     = false;
            this.preloader   = false;
            this.fieldValue  = null;
            this.placeholder = this.$placeholder('file', 'csv');
            this.headers     = [];
            this.previewData = [];
            this.mapping     = {
                transaction_date: '',
                transaction_details: '',
                withdrawal_amount: '',
                deposit_amount: '',
                balance: '',
                description: ''
            };
        },
    }
}
</script>
